/** Shared capability helpers (UX C5, spec 3.12). Moved out of
 *  components/gis-queue/types.ts so non-queue surfaces (detail panels, map
 *  assignment, secondary review) can gate controls without importing queue
 *  types; types.ts re-exports these for the gis-queue components.
 *
 *  Server enforcement is the authority (GIS 2A spec §2) — this only shapes
 *  the UI. The idiom is DISABLED WITH TOOLTIP, never hidden. */

export type Capability =
  | 'gis.queue.view'
  | 'gis.triage'
  | 'gis.triage.bulk'
  | 'gis.override'
  | 'gis.cr.withdraw'
  // UX remediation C5 (spec 3.12): bulk verification sweeps
  // (verify-meters + POST /verify/bulk).
  | 'topology.verify.bulk'
  // OMS operator roles (migration 035; core/authz.py role_granted).
  // oms.admin implies dispatch; oms.phi is its own grant.
  | 'oms.dispatch'
  | 'oms.admin'
  | 'oms.phi'

export function hasCapability(
  capabilities: readonly string[] | undefined | null,
  capability: Capability,
): boolean {
  return !!capabilities && capabilities.includes(capability)
}

export interface CapabilityInfo {
  capability: Capability
  label: string
  description: string
}

/** Every grantable capability, grouped for the admin role editor (Users
 *  page). Must list exactly the server vocabulary
 *  (services/capabilities.py ALL_CAPABILITIES) — the server 422s anything
 *  else. */
export const CAPABILITY_GROUPS: { title: string; items: CapabilityInfo[] }[] = [
  {
    title: 'Outage management',
    items: [
      {
        capability: 'oms.dispatch',
        label: 'Dispatcher',
        description: 'Confirm, close, cancel, merge and split outages; create outages.',
      },
      {
        capability: 'oms.admin',
        label: 'Outage admin',
        description: 'Export outage and reliability reports; unmatched-signals report. Includes Dispatcher.',
      },
      {
        capability: 'oms.phi',
        label: 'Critical-customer details',
        description: 'See medical / critical-customer details. Every view is logged.',
      },
    ],
  },
  {
    title: 'GIS corrections',
    items: [
      { capability: 'gis.queue.view', label: 'View GIS queue', description: 'See the GIS findings queue.' },
      { capability: 'gis.triage', label: 'Triage findings', description: 'Resolve individual findings.' },
      { capability: 'gis.triage.bulk', label: 'Bulk triage', description: 'Resolve many findings at once.' },
      {
        capability: 'gis.override',
        label: 'Override GIS values',
        description: 'Adopt, assign or revert values and connections.',
      },
      {
        capability: 'gis.cr.withdraw',
        label: 'Withdraw change requests',
        description: 'Withdraw change requests sent to GIS.',
      },
      {
        capability: 'topology.verify.bulk',
        label: 'Bulk verification',
        description: 'Run bulk meter-verification sweeps.',
      },
    ],
  },
]

/** The OMS dispatcher role: operator actions on outages (confirm / close /
 *  cancel / merge / split / create / edit). Mirrors core/authz.py — an
 *  `oms.admin` grant escalates to dispatcher. */
export function canDispatch(
  capabilities: readonly string[] | undefined | null,
): boolean {
  return (
    hasCapability(capabilities, 'oms.dispatch') ||
    hasCapability(capabilities, 'oms.admin')
  )
}

/** The exact disabled-control tooltip idiom established by the gis-queue
 *  components (ActionButtons/BulkBar/CrPanel): apostrophes around the name. */
export function capabilityTooltip(capability: Capability): string {
  return `Requires the '${capability}' capability`
}
